import ErrorMessage from '@/_application/core/components/ui/error-message';
import ProductItemSearch from '@/_application/modules/products/components/product-item-search';
import SearchItemLoader from '@/_application/modules/products/components/search/search-item-loader';
import {ProductListItemsQuery} from '@/framework/gql/products.graphql';
import {useProductListItems} from '@/framework/product';
import {Combobox, Transition} from '@headlessui/react';
import {FC, Fragment, useEffect, useState, useTransition} from 'react';
import {getImageAssetUrl} from '@/_application/core/utils/common';
import NotFound from '@/_application/core/components/ui/not-found';
import {useAtom} from 'jotai';
import {isSearchLoadingAtom} from '@/store/search-atom';

interface SearchViewProps {
    searchQuery: string;
}

const babySadImage = getImageAssetUrl('images/baby-sad.png');

const SearchView: FC<SearchViewProps> = ({searchQuery}) => {
    const [products, setProducts] = useState<ProductListItemsQuery['products']>();
    const [, setIsSearchLoading] = useAtom(isSearchLoadingAtom);
    const [, startTransition] = useTransition();
    const variables = {
        search: searchQuery.trim(),
        pageSize: 20,
    };

    const {
        isLoading: isfetchingProduct,
        fetchData,
        products: fetchedDataProducts,
        error,
    } = useProductListItems({
        ...variables,
    });

    useEffect(() => {
        if (searchQuery) {
            startTransition(() => {
                fetchData();
            });
        } else {
            startTransition(() => {
                setProducts({items: []});
            });
        }
    }, [searchQuery]);

    useEffect(() => {
        if (fetchedDataProducts && fetchedDataProducts !== products) {
            startTransition(() => setProducts(fetchedDataProducts));
        }
    }, [fetchedDataProducts, products]);

    useEffect(() => {
        setIsSearchLoading(isfetchingProduct);
    }, [isfetchingProduct]);

    const noResult = !isfetchingProduct && !products?.items?.length;

    if (error) return <ErrorMessage message={error.message} />;

    return (
        <Transition
            as={Fragment}
            enter="transition-opacity duration-75"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <Combobox.Options
                className={`absolute top-11 mt-1 w-full rounded-[20px] bg-white px-[10px]
          pb-1 pt-5 text-base shadow-lg ring-1 ring-black/5 after:absolute after:left-[20px]
          after:top-[-8px] after:z-10 after:border-x-8 after:border-b-8 after:border-t-0
          after:border-solid after:border-x-transparent after:border-b-white focus:outline-none sm:text-sm`}>
                <div className="max-h-80 overflow-auto pr-2">
                    {isfetchingProduct && <SearchItemLoader />}
                    {noResult ? (
                        <NotFound
                            text="text-not-found"
                            className="mx-auto w-7/12"
                            imageUrl={babySadImage}
                            size="small"
                        />
                    ) : (
                        products?.items?.map((product: any) => (
                            <Combobox.Option value={product} key={product?.sku}>
                                <ProductItemSearch product={product} />
                            </Combobox.Option>
                        ))
                    )}
                </div>
            </Combobox.Options>
        </Transition>
    );
};

export default SearchView;
